import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Button, Input, Badge } from '@img/ui';
import { useSession } from '../lib/auth-client';
import { api, type ApiSuccess } from '../lib/api';
import { SAUDI_CITIES } from '../lib/saudi-locations';
import { SEO } from '../components/SEO';

interface BrokerApplication {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  fullName: string;
  city: string;
  falLicenseNumber: string;
  rejectReason: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

const SPECIALTY_OPTIONS = ['سكني', 'تجاري', 'أراضي', 'استثماري', 'فلل', 'شقق', 'مكاتب'];

export function BecomeBrokerPage() {
  const { data: session } = useSession();
  const user = session?.user as { id: string; name: string; role: string; email: string; phone?: string } | undefined;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const isAuthed = !!user;
  const isBroker = user?.role === 'BROKER';

  // 📥 طلبي الحالي
  const { data: appResp } = useQuery({
    queryKey: ['me', 'broker-application'],
    queryFn: () => api.get<ApiSuccess<BrokerApplication | null>>('/api/brokers/me/application'),
    enabled: isAuthed,
  });
  const application = appResp?.data;

  // 📝 الحقول
  const [form, setForm] = useState({
    fullName:         user?.name ?? '',
    phone:            user?.phone ?? '',
    email:            user?.email ?? '',
    city:             '',
    falLicenseNumber: '',
    falLicenseFile:   '',
    falLicenseExpiry: '',
    yearsExperience:  0,
    agencyName:       '',
    citiesServed:     [] as string[],
    specialties:      [] as string[],
    motivation:       '',
    checkAgreementSigned:  false,
    checkRegaCompliance:   false,
    checkNoLegalIssues:    false,
    checkAcceptCommission: false,
  });

  const submitM = useMutation({
    mutationFn: () => api.post('/api/brokers/apply', {
      ...form,
      falLicenseFile:   form.falLicenseFile || null,
      falLicenseExpiry: form.falLicenseExpiry ? new Date(form.falLicenseExpiry).toISOString() : null,
      email:            form.email || null,
      agencyName:       form.agencyName || null,
      motivation:       form.motivation || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'broker-application'] });
    },
  });

  const cancelM = useMutation({
    mutationFn: () => api.post('/api/brokers/me/application/cancel', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'broker-application'] }),
  });

  const allChecked = form.checkAgreementSigned && form.checkRegaCompliance &&
                     form.checkNoLegalIssues && form.checkAcceptCommission;
  const requiredFilled = useMemo(() => (
    form.fullName.length >= 3 &&
    form.phone.length >= 10 &&
    form.city.length > 0 &&
    form.falLicenseNumber.length >= 3
  ), [form]);
  const canSubmit = isAuthed && allChecked && requiredFilled && !submitM.isPending;

  // 🚫 لو المستخدم مسوّق بالفعل
  if (isBroker) {
    return (
      <div className="container mx-auto max-w-3xl py-12 px-4">
        <Card>
          <CardBody>
            <div className="text-center py-8">
              <div className="text-5xl mb-3">🎉</div>
              <h2 className="text-2xl font-black text-navy-dark mb-2">أنت مسوّق معتمد بالفعل</h2>
              <p className="text-gray-600 mb-6">حسابك مفعّل كمسوّق على المنصة. اذهب للوحتك لإدارة العقارات والعملاء.</p>
              <Link to="/dashboard"><Button variant="primary">↩ الذهاب للوحة</Button></Link>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  // 🚫 لو الزائر غير مسجّل
  if (!isAuthed) {
    return (
      <div className="container mx-auto max-w-3xl py-12 px-4">
        <Card>
          <CardBody>
            <div className="text-center py-8">
              <div className="text-5xl mb-3">🔐</div>
              <h2 className="text-2xl font-black text-navy-dark mb-2">يجب تسجيل الدخول أولاً</h2>
              <p className="text-gray-600 mb-6">للتقديم للتحوّل لمسوّق عقاري معتمد، يرجى تسجيل الدخول أو إنشاء حساب.</p>
              <div className="flex gap-3 justify-center">
                <Link to="/login"><Button variant="primary">تسجيل الدخول</Button></Link>
                <Link to="/register"><Button variant="outline">إنشاء حساب</Button></Link>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  // ✅ نجاح التقديم
  if (submitM.isSuccess && application?.status === 'PENDING') {
    return (
      <div className="container mx-auto max-w-3xl py-12 px-4">
        <Card className="!border-emerald-500 !border-2">
          <CardBody>
            <div className="text-center py-8">
              <div className="text-5xl mb-3">⏳</div>
              <h2 className="text-2xl font-black text-emerald-700 mb-2">تم استلام طلبك بنجاح</h2>
              <p className="text-gray-700 mb-6">سنراجع طلبك ورخصتك خلال ٣–٧ أيام عمل، وسنُعلمك بالنتيجة عبر الإشعارات.</p>
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                ↩ الذهاب للوحة
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  // 📊 لو فيه طلب جاري
  if (application && application.status === 'PENDING') {
    return (
      <div className="container mx-auto max-w-3xl py-12 px-4">
        <Card className="!border-amber-400 !border-2">
          <CardBody>
            <div className="text-center py-6">
              <div className="text-5xl mb-3">⏳</div>
              <Badge color="amber">قيد المراجعة</Badge>
              <h2 className="text-2xl font-black text-navy-dark mt-3 mb-1">طلبك قيد المراجعة</h2>
              <p className="text-gray-600 text-sm">
                قُدّم بتاريخ {new Date(application.createdAt).toLocaleDateString('ar-SA')}
              </p>
              <div className="mt-6 p-4 bg-gray-50 rounded-xl text-sm text-right">
                <div className="grid grid-cols-2 gap-3">
                  <div><span className="text-gray-500">الاسم:</span> <b>{application.fullName}</b></div>
                  <div><span className="text-gray-500">المدينة:</span> <b>{application.city}</b></div>
                  <div><span className="text-gray-500">رخصة فال:</span> <b dir="ltr">{application.falLicenseNumber}</b></div>
                </div>
              </div>
              <div className="mt-6">
                <Button variant="ghost" onClick={() => cancelM.mutate()} loading={cancelM.isPending}>
                  ✕ إلغاء الطلب
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  // 📊 لو الطلب السابق مرفوض — يقدر يقدّم جديد بس نعرض السبب
  const showRejection = application?.status === 'REJECTED' && application.rejectReason;

  return (
    <div className="container mx-auto max-w-3xl py-12 px-4">
      <SEO
        title="انضم كمسوّق عقاري معتمد — شركة صورة العقارية 💼"
        description="انضم لشبكة مسوّقي شركة صورة العقاريين. مسوّقون معتمدون من فال (الهيئة العامة للعقار). شروط الانضمام: رخصة فال سارية، خبرة عقارية، التزام بالأنظمة. منصة قوية، عمولات تنافسية، دعم تسويقي كامل."
        keywords="انضم كمسوّق عقاري, مسوّق عقاري معتمد, رخصة فال, FAL license, الهيئة العامة للعقار, REGA, مسوّق عقاري السعودية, وسيط عقاري, تسجيل وسيط, الانضمام لشركة عقارية, عمل عقاري السعودية, وظائف عقارية, شركة صورة العقارية, img.sa"
        canonical="https://img.sa/become-broker"
        jsonLd={{
          '@type': 'JobPosting',
          title: 'مسوّق عقاري معتمد',
          description: 'انضم لشبكة مسوّقي شركة صورة العقاريين. مطلوب رخصة فال سارية وخبرة عقارية.',
          datePosted: new Date().toISOString().slice(0, 10),
          employmentType: 'CONTRACTOR',
          hiringOrganization: {
            '@type': 'Organization',
            name: 'شركة صورة العقارية',
            sameAs: 'https://img.sa',
          },
          jobLocation: {
            '@type': 'Place',
            address: { '@type': 'PostalAddress', addressCountry: 'SA' },
          },
          applicantLocationRequirements: { '@type': 'Country', name: 'SA' },
          qualifications: 'رخصة فال سارية من الهيئة العامة للعقار، خبرة عقارية',
          industry: 'Real Estate',
        }}
      />
      {/* الترويسة */}
      <div className="text-center mb-8">
        <div className="text-5xl mb-3">💼</div>
        <h1 className="text-3xl font-black text-navy-dark mb-2">انضم لشبكة مسوّقي صورة العقاريين</h1>
        <p className="text-gray-600">رخصة فال + خبرة + التزام بأنظمة الهيئة العامة للعقار = مسوّق معتمد</p>
      </div>

      {/* بانر سبب الرفض السابق */}
      {showRejection && (
        <Card className="mb-6 !bg-red-50 !border-red-300">
          <CardBody>
            <div className="flex items-start gap-3">
              <div className="text-2xl">❌</div>
              <div>
                <div className="font-bold text-red-700 mb-1">تم رفض طلبك السابق</div>
                <div className="text-sm text-red-600 mb-2">السبب: {application?.rejectReason}</div>
                <div className="text-xs text-gray-600">يمكنك معالجة الملاحظات وتقديم طلب جديد.</div>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* بانر خطأ submit */}
      {submitM.isError && (
        <Card className="mb-6 !bg-red-50 !border-red-300">
          <CardBody>
            <div className="text-sm text-red-700">
              ⚠️ {(submitM.error as Error).message || 'حدث خطأ أثناء الإرسال'}
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody className="space-y-6">
          {/* البيانات الأساسية */}
          <Section title="📋 البيانات الأساسية">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="الاسم الكامل *">
                <Input value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
              </Field>
              <Field label="الجوال *">
                <Input dir="ltr" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+9665…" />
              </Field>
              <Field label="الإيميل (اختياري)">
                <Input dir="ltr" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </Field>
              <Field label="المدينة *">
                <select className="w-full px-3 py-2 border rounded-lg" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}>
                  <option value="">اختر…</option>
                  {SAUDI_CITIES.map((cityObj) => (
                    <option key={typeof cityObj === 'string' ? cityObj : cityObj.name} value={typeof cityObj === 'string' ? cityObj : cityObj.name}>
                      {typeof cityObj === 'string' ? cityObj : cityObj.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </Section>

          {/* رخصة فال */}
          <Section title="🏛️ رخصة فال (إلزامية)">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="رقم الرخصة *">
                <Input dir="ltr" value={form.falLicenseNumber} onChange={(e) => setForm({ ...form, falLicenseNumber: e.target.value })} placeholder="1100…" />
              </Field>
              <Field label="تاريخ الانتهاء">
                <Input type="date" value={form.falLicenseExpiry} onChange={(e) => setForm({ ...form, falLicenseExpiry: e.target.value })} />
              </Field>
            </div>
            <Field label="رابط ملف الرخصة (PDF أو صورة) — اختياري حالياً">
              <Input dir="ltr" value={form.falLicenseFile} onChange={(e) => setForm({ ...form, falLicenseFile: e.target.value })} placeholder="https://…" />
            </Field>
            <p className="text-xs text-gray-500">
              ⓘ بعد التقديم تقدر ترفع ملف الرخصة من لوحتك. الإدارة تتحقق من الرخصة عند المراجعة.
            </p>
          </Section>

          {/* الخبرة */}
          <Section title="🧠 الخبرة والتخصص">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="سنوات الخبرة">
                <Input type="number" min={0} max={80} value={form.yearsExperience}
                       onChange={(e) => setForm({ ...form, yearsExperience: Number(e.target.value) })} />
              </Field>
              <Field label="اسم المكتب/الشركة (اختياري)">
                <Input value={form.agencyName} onChange={(e) => setForm({ ...form, agencyName: e.target.value })} />
              </Field>
            </div>
            <Field label="التخصصات">
              <div className="flex flex-wrap gap-2">
                {SPECIALTY_OPTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      const has = form.specialties.includes(s);
                      setForm({
                        ...form,
                        specialties: has ? form.specialties.filter((x) => x !== s) : [...form.specialties, s],
                      });
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-bold transition border ${
                      form.specialties.includes(s)
                        ? 'bg-navy-dark text-white border-navy-dark'
                        : 'bg-white text-navy-dark border-gray-300 hover:border-gold'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="لماذا تريد الانضمام لشبكة صورة؟ (اختياري)">
              <textarea className="w-full px-3 py-2 border rounded-lg min-h-[100px]"
                        value={form.motivation}
                        onChange={(e) => setForm({ ...form, motivation: e.target.value })} />
            </Field>
          </Section>

          {/* Checklist */}
          <Section title="✅ تأكيد الالتزامات (الأربعة إلزامية)">
            <div className="space-y-2">
              <CheckRow
                checked={form.checkAgreementSigned}
                onChange={(v) => setForm({ ...form, checkAgreementSigned: v })}
                label="أؤكد التوقيع على اتفاقية الشراكة وأوافق على شروط المنصة"
              />
              <CheckRow
                checked={form.checkRegaCompliance}
                onChange={(v) => setForm({ ...form, checkRegaCompliance: v })}
                label="ألتزم بأنظمة الهيئة العامة للعقار (REGA) في جميع تعاملاتي"
              />
              <CheckRow
                checked={form.checkNoLegalIssues}
                onChange={(v) => setForm({ ...form, checkNoLegalIssues: v })}
                label="أُقرّ بعدم وجود قضايا أو مخالفات قضائية تمنعني من ممارسة المهنة"
              />
              <CheckRow
                checked={form.checkAcceptCommission}
                onChange={(v) => setForm({ ...form, checkAcceptCommission: v })}
                label="أوافق على هيكل العمولة المنصوص عليه في اتفاقية المسوّق"
              />
            </div>
          </Section>

          {/* الأزرار */}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <Link to="/"><Button variant="ghost">إلغاء</Button></Link>
            <Button
              variant="primary"
              disabled={!canSubmit}
              loading={submitM.isPending}
              onClick={() => submitM.mutate()}
            >
              📤 إرسال الطلب للمراجعة
            </Button>
          </div>
          {!isAuthed && <p className="text-xs text-red-600 text-end">يجب تسجيل الدخول</p>}
          {!requiredFilled && <p className="text-xs text-amber-700 text-end">⚠️ بعض الحقول الأساسية غير مكتملة</p>}
          {!allChecked && requiredFilled && <p className="text-xs text-amber-700 text-end">⚠️ يجب الموافقة على جميع البنود الأربعة</p>}
        </CardBody>
      </Card>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="font-black text-navy-dark mb-3 text-base">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-bold text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

function CheckRow({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="flex items-start gap-2.5 p-3 rounded-lg bg-gray-50 hover:bg-gold/5 cursor-pointer border border-gray-200">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-5 h-5 rounded text-navy-dark cursor-pointer"
      />
      <span className="text-sm text-navy-dark flex-1">{label}</span>
    </label>
  );
}
